import React, { useState, ChangeEvent } from "react";
import { headerStyle, sectionStyle } from "./formConstants";
import InstaColors, { useSharedValue } from "../components/Constants/constants";
import "../Forms/style.css";
import { FormCheckBox, FormDate, FormInput, FormTitle, createRequiredLabel } from "./common";
import CountryDropdown from "./CountryDropdown";
import { W9FormModel } from "../Models/W9Models";
import { useW9Form } from "../Hooks/useW9Form";
import { W9Service } from "../services/W9Service";
import { validateTINFormat } from "../utils/formUtils";

interface W9FormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const W9Form: React.FC<W9FormProps> = ({
  selectedRowData,
  row,
  selectedIndex = 0,
  onSaveSuccess,
}) => {
  const { isMenuOpen } = useSharedValue();

  // Use W9 form hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useW9Form({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  // Watch for changes in selectedRowData and update form fields accordingly
  React.useEffect(() => {
    if (selectedRowData) {
      // Check for foreign partners field with various potential names
      const foreignPartnersKeys = [
        "W9-3B_foreign_partners",
        "foreign_partners",
        "W9_3B_foreign_partners",
        "W9-3B-foreign_partners",
        "foreign_partners_owners_beneficiaries",
        "partnership_trust_estate_foreign_partners",
        "W9-foreign_partners",
        "foreign_partner_question",
        "W9-Line3B-foreign_partners",
      ];

      foreignPartnersKeys.forEach((key) => {
        if (selectedRowData[key] !== undefined) {
          console.log(`Found foreign partners field: ${key} = ${selectedRowData[key]}`);
        }
      });

      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        console.log("Updating UniqueIdentifier in W9 form:", {
          from: currentUniqueIdentifier,
          to: newUniqueIdentifier,
        });
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Special handling for foreign partners field - convert various formats to yes/no
        if (key === "W9-3B_foreign_partners" || key === "foreign_partners") {
          let processedValue = "";
          if (
            value === "yes" ||
            value === "selected" ||
            value === "true" ||
            value === true ||
            value === 1
          ) {
            processedValue = "yes";
          } else if (
            value === "no" ||
            value === "" ||
            value === false ||
            value === 0 ||
            value === null
          ) {
            processedValue = "no";
          } else if (value) {
            processedValue = value.toString();
          }

          if (processedValue && processedValue !== currentValue) {
            console.log("Updating foreign partners field:", {
              key,
              originalValue: value,
              processedValue,
              currentValue,
            });
            updateField("W9-3B_foreign_partners", processedValue);
          }
          return;
        }

        // Only update if the value has actually changed and is not empty
        // Handle both string and non-string values now
        if (value !== undefined && value !== null && value !== currentValue) {
          const stringValue = typeof value === "string" ? value : value.toString();
          if (stringValue.trim()) {
            updateField(key, stringValue);
          }
        }
      });
    }
  }, [selectedRowData, updateField]);

  // Auto-set needToBeSigned to always be "yes" for W9 forms
  React.useEffect(() => {
    if (formData.needToBeSigned !== "yes") {
      updateField("needToBeSigned", "yes");
    }
  }, [formData.needToBeSigned, updateField]);

  // Auto-assign any state value to US state field (W9 is for US entities only)
  React.useEffect(() => {
    const nonUSState = formData["Permanent address state NonUS"];
    const usState = formData["Permanent address state USCA"];

    // If NonUS state has a value but US state is empty, move it to US state
    if (nonUSState && typeof nonUSState === "string" && nonUSState.trim() && !usState) {
      console.log("Moving state value from NonUS to US field:", nonUSState);
      updateField("Permanent address state USCA", nonUSState);
      updateField("Permanent address state NonUS", ""); // Clear NonUS field
    }

    // If any generic "state" field exists, assign it to US state
    const genericState =
      formData["state"] || formData["State"] || formData["Permanent address state"];
    if (genericState && typeof genericState === "string" && genericState.trim() && !usState) {
      console.log("Assigning generic state value to US field:", genericState);
      updateField("Permanent address state USCA", genericState);
    }
  }, [
    formData["Permanent address state NonUS"],
    formData["Permanent address state USCA"],
    formData["state"],
    formData["State"],
    formData["Permanent address state"],
    updateField,
  ]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, type, value, checked } = e.target as HTMLInputElement;
    const newValue = type === "checkbox" ? checked : value;

    // Update the model through the hook
    updateField(name, newValue);

    // Handle special logic for tax classification
    if (name.startsWith("W9-Tax_classification_")) {
      if (type === "checkbox" && checked) {
        // Uncheck other tax classifications when one is selected (single choice behavior)
        const taxClassifications = [
          "W9-Tax_classification_individual_sole_proprietor",
          "W9-Tax_classification_c_corporation",
          "W9-Tax_classification_s_corporation",
          "W9-Tax_classification_partnership",
          "W9-Tax_classification_trust_estate",
          "W9-Tax_classification_llc",
          "W9-Tax_classification_other",
        ];

        const updates: Record<string, boolean> = {};
        taxClassifications.forEach((field) => {
          updates[field] = field === name;
        });
        updateMultipleFields(updates);

        // Handle name field logic and federal tax classification based on tax classification
        const federalUpdates: Record<string, boolean> = {};

        // Reset federal checkboxes first
        federalUpdates["W9-Federal_c_corporation"] = false;
        federalUpdates["W9-Federal_s_corporation"] = false;
        federalUpdates["W9-Federal_partnership"] = false;

        if (name === "W9-Tax_classification_individual_sole_proprietor") {
          federalUpdates["W9-Federal_c_corporation"] = false;
          federalUpdates["W9-Federal_s_corporation"] = false;

          federalUpdates["W9-Federal_partnership"] = false;
          // For individuals, clear organization name and business name, show first/last name
          if (formData["Name of organization"]) {
            const nameParts = formData["Name of organization"].toString().split(" ");
            // Store business name in a temporary field so we can restore it later
            const businessName = formData["W9-Business_name"] || "";
            updateMultipleFields({
              "First name and middle name": nameParts.slice(0, -1).join(" ") || nameParts[0] || "",
              "Last name": nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
              "Name of organization": "",
              "W9-Business_name": "",
              _temp_business_name: businessName, // Store for later restoration
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              "Name of organization": "",
              // 'W9-Business_name': '',
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_c_corporation") {
          // For C Corporation, set federal C corporation and handle name fields
          federalUpdates["W9-Federal_c_corporation"] = true;
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_s_corporation") {
          // For S Corporation, set federal S corporation and handle name fields
          federalUpdates["W9-Federal_s_corporation"] = true;
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_partnership") {
          // For Partnership, set federal partnership and handle name fields
          // federalUpdates['W9-Federal_partnership'] = true;
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_trust_estate") {
          // For Trust/Estate, clear all federal checkboxes and handle name fields
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_llc") {
          // For LLC, check the LLC value field to determine federal classification and handle name fields
          const llcValue =
            formData["W9_Tax_classification_llc_value"] ||
            formData["W9-Tax_classification_llc_value"];
          if (llcValue) {
            const llcValueLower = llcValue.toString().toLowerCase();
            if (llcValueLower.includes("p")) {
              federalUpdates["W9-Federal_partnership"] = true;
            } else if (llcValueLower.includes("c")) {
              federalUpdates["W9-Federal_c_corporation"] = true;
            } else if (llcValueLower.includes("s")) {
              federalUpdates["W9-Federal_s_corporation"] = true;
            }
          }
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        } else if (name === "W9-Tax_classification_other") {
          // For Other, clear all federal checkboxes and handle name fields
          const firstName = formData["First name and middle name"] || "";
          const lastName = formData["Last name"] || "";
          // Restore business name if it was saved temporarily
          const tempBusinessName = formData["_temp_business_name"] || "";
          if (firstName || lastName) {
            updateMultipleFields({
              "Name of organization": `${firstName} ${lastName}`.trim(),
              "First name and middle name": "",
              "Last name": "",
              "W9-Business_name": tempBusinessName, // Restore business name
              _temp_business_name: "", // Clear temp field
              ...federalUpdates,
            });
          } else {
            updateMultipleFields({
              ...federalUpdates,
            });
          }
        }
      }
    }

    // Auto-detect taxpayer type based on which field has a value
    if (name === "Employer identification number") {
      if (value.trim()) {
        updateField("Taxpayer type", "EIN");

        // Validate EIN format
        const tinValue = value.toString().trim();
        const validation = validateTINFormat(tinValue, "EIN");
        setTinValidationMessage(validation.message);

        // If valid and auto-detected type is different, update it
        if (validation.isValid && validation.detectedType && validation.detectedType !== "EIN") {
          updateField("Taxpayer type", validation.detectedType);
        }
      } else {
        setTinValidationMessage(null);
      }
    } else if (name === "Social security number") {
      if (value.trim()) {
        updateField("Taxpayer type", "SSN/ITIN");

        // Validate SSN/ITIN format
        const tinValue = value.toString().trim();
        const validation = validateTINFormat(tinValue, "SSN/ITIN");
        setTinValidationMessage(validation.message);

        // If valid and auto-detected type is different, update it
        if (
          validation.isValid &&
          validation.detectedType &&
          validation.detectedType !== "SSN/ITIN"
        ) {
          let formType = validation.detectedType;
          if (formType === "SSN" || formType === "ITIN") {
            formType = "SSN/ITIN";
          }
          updateField("Taxpayer type", formType);
        }
      } else {
        setTinValidationMessage(null);
      }
    } else if (name === "Taxpayer type") {
      // Clear TIN validation when taxpayer type changes
      setTinValidationMessage(null);
    }

    // Handle LLC value changes for federal tax classification
    if (name === "W9_Tax_classification_llc_value" || name === "W9-Tax_classification_llc_value") {
      // Only update federal classification if LLC is currently selected
      if (formData["W9-Tax_classification_llc"]) {
        const federalUpdates: Record<string, boolean> = {
          "W9-Federal_c_corporation": false,
          "W9-Federal_s_corporation": false,
          "W9-Federal_partnership": false,
        };

        if (value && value.toString().trim()) {
          const llcValueLower = value.toString().toLowerCase();
          if (llcValueLower.includes("p")) {
            federalUpdates["W9-Federal_partnership"] = true;
          } else if (llcValueLower.includes("c")) {
            federalUpdates["W9-Federal_c_corporation"] = true;
          } else if (llcValueLower.includes("s")) {
            federalUpdates["W9-Federal_s_corporation"] = true;
          }
        }

        updateMultipleFields(federalUpdates);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous validation errors
    setShowValidationErrors(false);

    if (!validate()) {
      setShowValidationErrors(true);
      console.log("Form validation failed");
      alert("Please fill in all required fields before submitting.");
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = {};

      // Get all current form values from the model (which is always up-to-date)
      Object.keys(model).forEach((key) => {
        currentFormValues[key] = (model as any)[key];
      });

      // Remove specific fields before saving

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;
      currentFormValues["Permanent city address"] = "";
      currentFormValues["Mailing address city"] = "";
      // Create a new model with the current form values
      const saveModel = new W9FormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W9Service.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-9 form:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("W9-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Unique Identifier */}
            <section style={sectionStyle}>
              <FormInput
                label={createRequiredLabel("Unique Form Identifier")}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={(formData["UniqueIdentifier"] as string) || ""}
                onChange={handleChange}
              />
              {showValidationErrors && errors["UniqueIdentifier"] && (
                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                  {errors["UniqueIdentifier"]}
                </div>
              )}
            </section>

            <section style={sectionStyle}>
              <FormTitle value={"Part I Identification of Beneficial Owner"} />
              <FormInput
                label={"First name and middle name:"}
                id={"First name and middle name"}
                name={"First name and middle name"}
                value={(formData["First name and middle name"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Last name:"}
                id={"Last name"}
                name={"Last name"}
                value={(formData["Last name"] as string) || ""}
                onChange={handleChange}
              />
              {/* Only show Entity Name if Individual/Sole Proprietor is NOT selected */}
              {!formData["W9-Tax_classification_individual_sole_proprietor"] && (
                <FormInput
                  label={"Entity Name:"}
                  id={"Name of organization"}
                  name={"Name of organization"}
                  value={(formData["Name of organization"] as string) || ""}
                  onChange={handleChange}
                />
              )}
              <FormInput
                label={"Business name/disregarded entity name (if different):"}
                id={"W9-Business_name"}
                name={"W9-Business_name"}
                value={(formData["W9-Business_name"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country of Organization:"
                name="Country_of_incorporation/organization"
                id="Country_of_incorporation/organization"
                value={
                  typeof formData["Country_of_incorporation/organization"] === "string"
                    ? formData["Country_of_incorporation/organization"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Country_of_incorporation/organization",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>User Record types:</label>
                <FormCheckBox
                  label={"Corporation"}
                  name={"W9-Tax_classification_c_corporation"}
                  checked={!!formData["W9-Tax_classification_c_corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Individual/Sole Proprietor"}
                  name={"W9-Tax_classification_individual_sole_proprietor"}
                  checked={!!formData["W9-Tax_classification_individual_sole_proprietor"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Limited Liability Company"}
                  name={"W9-Tax_classification_llc"}
                  checked={!!formData["W9-Tax_classification_llc"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Partnership"}
                  name={"W9-Tax_classification_partnership"}
                  checked={!!formData["W9-Tax_classification_partnership"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Trust/Estate"}
                  name={"W9-Tax_classification_trust_estate"}
                  checked={!!formData["W9-Tax_classification_trust_estate"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Other"}
                  name={"W9-Tax_classification_other"}
                  checked={!!formData["W9-Tax_classification_other"]}
                  onChange={handleChange}
                />
                <FormInput
                  label={"Other description:"}
                  id={"W9-Tax_classification_other_description"}
                  name={"W9-Tax_classification_other_description"}
                  value={(formData["W9-Tax_classification_other_description"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  US Federal Tax Classifications:
                </label>
                <FormCheckBox
                  label={"S-Corporation"}
                  name={"W9-Federal_s_corporation"}
                  checked={!!formData["W9-Federal_s_corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"C-Corporation"}
                  name={"W9-Federal_c_corporation"}
                  checked={!!formData["W9-Federal_c_corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Partnership"}
                  name={"W9-Federal_partnership"}
                  checked={!!formData["W9-Federal_partnership"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Are you providing this form to a partnership, trust or estate, and if so, do you
                  have any foreign partners, owners, or beneficiaries?
                </label>
                <br />
                <label style={{ marginRight: 16 }}>
                  <input
                    type="radio"
                    name="W9-3B_foreign_partners"
                    value="yes"
                    checked={formData["W9-3B_foreign_partners"] === "yes"}
                    onChange={handleChange}
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="W9-3B_foreign_partners"
                    value="no"
                    checked={formData["W9-3B_foreign_partners"] === "no"}
                    onChange={handleChange}
                  />{" "}
                  No
                </label>
              </div>

              {/* Chapter 4 Status */}
              <FormInput
                label="Chapter 4 Status:"
                id="Chapter4Status"
                name="Chapter4Status"
                value={(formData["Chapter4Status"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Address section */}

            {/* Permanent address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Permanent address"} />

              {/* Additional permanent address fields from original form */}
              <FormInput
                label={"Line 1:"}
                id={"Permanent residence address"}
                name={"Permanent residence address"}
                value={(formData["Permanent residence address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent address city"}
                name={"Permanent address city"}
                value={(formData["Permanent address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non-U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Permanent address country"
                id="Permanent address country"
                value={
                  typeof formData["Permanent address country"] === "string"
                    ? formData["Permanent address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Permanent address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent address other"}
                name={"Permanent address other"}
                value={(formData["Permanent address other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Taxpayer identification section */}
            <section style={sectionStyle}>
              <FormTitle value={"Taxpayer identification"} />
              <FormInput
                label="Email:"
                id="Taxpayer email"
                name="Taxpayer email"
                value={(formData["Taxpayer email"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="Taxpayer type" style={{ display: "block", marginBottom: 4 }}>
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="EIN">EIN</option>
                  <option value="SSN/ITIN">SSN/ITIN</option>
                </select>
              </div>

              {/* Show EIN field when EIN is selected */}
              {formData["Taxpayer type"] === "EIN" && (
                <div style={{ marginBottom: 16 }}>
                  <FormInput
                    label="EIN:"
                    id="Employer identification number"
                    name="Employer identification number"
                    value={(formData["Employer identification number"] as string) || ""}
                    onChange={handleChange}
                    placeholder="XX-XXXXXXX"
                  />
                  {tinValidationMessage && (
                    <div
                      style={{
                        marginTop: 4,
                        padding: "6px 8px",
                        backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                        border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                        borderRadius: 4,
                        fontSize: "12px",
                        color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                      }}
                    >
                      {tinValidationMessage}
                    </div>
                  )}
                </div>
              )}
              {/* Show SSN field when SSN/ITIN is selected */}
              {formData["Taxpayer type"] === "SSN/ITIN" && (
                <div style={{ marginBottom: 16 }}>
                  <FormInput
                    label="SSN/ITIN:"
                    id="Social security number"
                    name="Social security number"
                    value={(formData["Social security number"] as string) || ""}
                    onChange={handleChange}
                    placeholder="XXX-XX-XXXX"
                  />
                  {tinValidationMessage && (
                    <div
                      style={{
                        marginTop: 4,
                        padding: "6px 8px",
                        backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                        border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                        borderRadius: 4,
                        fontSize: "12px",
                        color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                      }}
                    >
                      {tinValidationMessage}
                    </div>
                  )}
                </div>
              )}

              {/* Message when no type is selected */}
              {!formData["Taxpayer type"] && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: 4,
                    marginBottom: 16,
                    fontStyle: "italic",
                    color: "#6b7280",
                  }}
                >
                  Please select a type above to enter your taxpayer identification number.
                </div>
              )}

              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Applied for:</label>
                <label
                  style={{
                    marginRight: 16,
                    fontWeight: formData["Taxpayer applied for"] === "Unknown" ? 600 : undefined,
                  }}
                >
                  <input
                    type="radio"
                    name="Taxpayer applied for"
                    value="Unknown"
                    checked={formData["Taxpayer applied for"] === "Unknown"}
                    onChange={handleChange}
                  />{" "}
                  Unknown
                </label>
                <label style={{ marginRight: 16 }}>
                  <input
                    type="radio"
                    name="Taxpayer applied for"
                    value="Yes"
                    checked={formData["Taxpayer applied for"] === "Yes"}
                    onChange={handleChange}
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="Taxpayer applied for"
                    value="No"
                    checked={formData["Taxpayer applied for"] === "No"}
                    onChange={handleChange}
                  />{" "}
                  No
                </label>
              </div>
            </section>

            {/* Other information section */}
            <section style={sectionStyle}>
              <FormTitle value={"Other information"} />

              {/* Exempt payee code */}
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="W9-Exempt_payee_code" style={{ display: "block", marginBottom: 4 }}>
                  Exempt payee code (if any):
                </label>
                <select
                  id="W9-Exempt_payee_code"
                  name="W9-Exempt_payee_code"
                  value={
                    typeof formData["W9-Exempt_payee_code"] === "string"
                      ? formData["W9-Exempt_payee_code"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W9-Exempt_payee_code",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                  <option value="13">13</option>
                </select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    name="Exempt payee multiple"
                    checked={!!formData["Exempt payee multiple"]}
                    onChange={handleChange}
                  />{" "}
                  Multiple selections:
                </label>
                <FormInput
                  label=""
                  id="Exempt payee multiple text"
                  name="Exempt payee multiple text"
                  value={(formData["Exempt payee multiple text"] as string) || ""}
                  onChange={handleChange}
                  placeholder="- Enter the text as it appears on the form -"
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>
                  <input
                    type="checkbox"
                    name="Exempt payee unknown"
                    checked={!!formData["Exempt payee unknown"]}
                    onChange={handleChange}
                  />{" "}
                  Unknown selection (illegible):
                </label>
              </div>
              {/* FATCA code */}
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="W9-Fatca_exemption_code"
                  style={{ display: "block", marginBottom: 4 }}
                >
                  Exemption from FATCA reporting code (if any):
                </label>
                <select
                  id="W9-Fatca_exemption_code"
                  name="W9-Fatca_exemption_code"
                  value={
                    typeof formData["W9-Fatca_exemption_code"] === "string"
                      ? formData["W9-Fatca_exemption_code"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W9-Fatca_exemption_code",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="E">E</option>
                  <option value="F">F</option>
                  <option value="G">G</option>
                  <option value="H">H</option>
                  <option value="I">I</option>
                  <option value="J">J</option>
                  <option value="K">K</option>
                  <option value="L">L</option>
                  <option value="M">M</option>
                </select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    name="FATCA multiple"
                    checked={!!formData["FATCA multiple"]}
                    onChange={handleChange}
                  />{" "}
                  Multiple selections:
                </label>
                <FormInput
                  label=""
                  id="FATCA multiple text"
                  name="FATCA multiple text"
                  value={(formData["FATCA multiple text"] as string) || ""}
                  onChange={handleChange}
                  placeholder="- Enter the text as it appears on the form -"
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>
                  <input
                    type="checkbox"
                    name="FATCA unknown"
                    checked={!!formData["FATCA unknown"]}
                    onChange={handleChange}
                  />{" "}
                  Unknown selection (illegible):
                </label>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="W9-Requester_name_address"
                  style={{ display: "block", marginBottom: 4 }}
                >
                  Requester's name and address:
                </label>
                <textarea
                  id="W9-Requester_name_address"
                  name="W9-Requester_name_address"
                  value={(formData["W9-Requester_name_address"] as string) || ""}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W9-Requester_name_address",
                        value: e.target.value,
                        type: "textarea",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    minHeight: 60,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    padding: 8,
                  }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="W9-Account_numbers" style={{ display: "block", marginBottom: 4 }}>
                  Account number(s) list:
                </label>
                <textarea
                  id="W9-Account_numbers"
                  name="W9-Account_numbers"
                  value={(formData["W9-Account_numbers"] as string) || ""}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W9-Account_numbers",
                        value: e.target.value,
                        type: "textarea",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    minHeight: 60,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    padding: 8,
                  }}
                />
              </div>
            </section>

            <section style={sectionStyle}>
              <FormTitle value={"Part II Certification"} />
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">Need to be signed:</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="needToBeSigned"
                      value="yes"
                      checked={true}
                      disabled={false}
                      onChange={() => {}} // No-op since it's always yes
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="needToBeSigned"
                      value="no"
                      checked={false}
                      disabled={false}
                      onChange={() => {}} // No-op since it's always yes
                    />{" "}
                    No
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">Is part II line 2 (a) crossed out:</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2aCrossedOut"
                      value="yes"
                      checked={formData.isPartII2aCrossedOut === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2aCrossedOut", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2aCrossedOut"
                      value="no"
                      checked={formData.isPartII2aCrossedOut === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2aCrossedOut", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">Is part II line 2 (b) crossed out:</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2bCrossedOut"
                      value="yes"
                      checked={formData.isPartII2bCrossedOut === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2bCrossedOut", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2bCrossedOut"
                      value="no"
                      checked={formData.isPartII2bCrossedOut === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2bCrossedOut", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">Is part II line 2 (c) crossed out:</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2cCrossedOut"
                      value="yes"
                      checked={formData.isPartII2cCrossedOut === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2cCrossedOut", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isPartII2cCrossedOut"
                      value="no"
                      checked={formData.isPartII2cCrossedOut === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isPartII2cCrossedOut", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">Is signed:</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <FormInput
                  label={"Signed Name:"}
                  id={"Print_name"}
                  value={formData["Print_name"]}
                  name={"Print_name"}
                  onChange={handleChange}
                />

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 12,
                      marginTop: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="Signature_unreadable"
                      checked={formData.Signature_unreadable || false}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signature_unreadable",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="Signature_unreadable">Check if signature is unreadable</label>
                  </div>
                </div>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "6px 16px",
                  backgroundColor: InstaColors.primary,
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "none",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default W9Form;
